import { useCallback, useEffect, useState } from 'react';
import { EstadoErro } from '@/components/patterns/states';
import { setAuthPort } from '@/data/auth';
import { iniciarKeycloak } from '@/data/auth/keycloak';
import { LogoCompilacoes } from './logo';

/**
 * Portão de entrada do modo `rest`: só autentica no Keycloak. As capabilities
 * vêm do próprio token (ver `data/auth/keycloak.ts`) e cada tela trata os seus
 * estados — não há passo de boot além do login.
 *
 * Enquanto autentica, mostra a marca: a logo se monta ponto a ponto (fade), o nome sobe e uma barrinha indica o
 * trabalho. Quando o login termina a tela some com um fade e o app aparece também com fade, sem salto.
 */

type Etapa = 'autenticando' | 'saindo' | 'pronto' | 'erro';

const DURACAO_SAIDA_MS = 240;
/** Tempo que a logo leva para se montar por inteiro (a última barra entra em ~940ms e o nome em ~980ms). */
const DURACAO_ANIMACAO_MS = 1100;

function pedeMenosMovimento(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function BootGate({ children }: { children: React.ReactNode }) {
  const [etapa, setEtapa] = useState<Etapa>('autenticando');
  const [erro, setErro] = useState<Error | null>(null);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let ativo = true;
    let temporizador: number | undefined;

    async function bootar() {
      const inicio = Date.now();
      try {
        setErro(null);
        setEtapa('autenticando');

        // `iniciarKeycloak` é single-flight: a segunda montagem do StrictMode
        // espera a MESMA Promise em vez de repetir o `init()` ou seguir sem
        // identificador.
        const sessao = await iniciarKeycloak();
        if (!ativo) return;
        setAuthPort(sessao.port);
        // Login rápido não corta a animação: espera a logo terminar de se montar antes de trocar para o app. Login
        // lento não paga espera nenhuma, e quem pede menos movimento não espera.
        const espera = pedeMenosMovimento() ? 0 : Math.max(0, DURACAO_ANIMACAO_MS - (Date.now() - inicio));
        temporizador = window.setTimeout(() => {
          if (!ativo) return;
          setEtapa('saindo');
          temporizador = window.setTimeout(() => ativo && setEtapa('pronto'), DURACAO_SAIDA_MS);
        }, espera);
      } catch (causa) {
        if (!ativo || (causa instanceof DOMException && causa.name === 'AbortError')) return;
        setErro(causa instanceof Error ? causa : new Error(String(causa)));
        setEtapa('erro');
      }
    }

    void bootar();
    return () => {
      ativo = false;
      window.clearTimeout(temporizador);
    };
  }, [tentativa]);

  const tentarDeNovo = useCallback(() => setTentativa((valor) => valor + 1), []);

  if (etapa === 'pronto') return <div className="cmp-aparecer h-full">{children}</div>;

  return (
    <div className="flex min-h-full items-center justify-center bg-bg-page p-6">
      {etapa === 'erro' ? (
        <div className="cmp-aparecer w-full max-w-lg rounded-card border border-border bg-bg-surface shadow-sm">
          <EstadoErro erro={erro} aoTentarDeNovo={tentarDeNovo} />
        </div>
      ) : (
        <div
          role="status"
          aria-live="polite"
          className="flex flex-col items-center gap-6 text-center transition-opacity ease-out"
          style={{ opacity: etapa === 'saindo' ? 0 : 1, transitionDuration: `${DURACAO_SAIDA_MS}ms` }}
        >
          <LogoCompilacoes animado className="size-24 drop-shadow-md" />
          <div className="cmp-entrada flex flex-col gap-1" style={{ animationDelay: '420ms' }}>
            <span className="text-[30px] font-bold leading-none tracking-[-0.02em] text-on-surface">Compilações</span>
            <span className="text-micro font-normal text-on-surface-muted">by Nasajon</span>
          </div>
          <div className="cmp-aparecer flex flex-col items-center gap-2" style={{ animationDelay: '760ms' }}>
            <div className="h-1 w-40 overflow-hidden rounded-full bg-border">
              <div className="cmp-progresso h-full w-1/3 rounded-full bg-primary" />
            </div>
            <span className="text-cell text-on-surface-muted">Autenticando…</span>
          </div>
        </div>
      )}
    </div>
  );
}
